import { useEffect, useMemo } from 'react'

import { useGetCloudReceivers } from '@apps-common/apis/portal/cloud-receivers'
import { ReceiverKpiContainer, ReceiversFilterButton, ReceiversRefetch } from '@apps-common/apps/portal/pages/receivers/components'

import { PullRefresh, RootHeaderButtons } from '@apps-common/components/common'
import { NavbarCompanyLogo } from '@apps-common/components/common/logos'
import { PageTitle } from '@apps-common/components/common/portal-title'
import { Header, LayoutContainer, LayoutPadding, VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { PageClearFilterButton } from '@apps-common/components/page'
import { commonIcons } from '@apps-common/config/icons'
import { RECEIVERS_QUEUE_SIZE_OPTION_10, RECEIVERS_QUEUE_SIZE_OPTION_5 } from '@apps-common/config/receivers'
import { TimerProvider } from '@apps-common/contexts'
import { useReceiversFilterStore } from '@apps-common/stores'
import { ICloudReceiverModel, IGatewayCompanyModel } from '@apps-common/types/portal/cloud-receiver'
import { ROUTE_RECEIVERS } from '@apps-radionet-portal/config/private-routes'
import { ReceiverCompanyView } from './ReceiverCompanyView'
import { ReceiverGatewayView } from './ReceiverGatewayView'
import { receiverParams } from './receiver-constants'

export const ReceiversView = () => {
  const { isLoading, isError, isRefetching, refetch, data } = useGetCloudReceivers({})

  const filter = useReceiversFilterStore()

  //   const companies = useMemo(() => {
  //     return (data?.gateways || [])
  //       .reduce(
  //         (acc: { label: string; value: string }[], curr) => [
  //           ...acc,
  //           ...curr.companies.map((c) => ({ label: c.companyName, value: c.companyId }))
  //         ],
  //         []
  //       )
  //       .filter((value, index, self) => self.findIndex((s) => s.value === value.value) === index)
  //       .sort((a, b) => sortAlphabetically(a, b, 'label'))
  //   }, [data])

  const { tab, serverId, companyId, queueSize, data: filterData, setData, setTab, clear, getFilteredByKeywords } = filter

  const cloudReceivers: ICloudReceiverModel | undefined = useMemo(() => {
    if (!data) return data

    const gateways = data?.gateways
      ?.filter((c) => {
        if (serverId) {
          return c.serverId === serverId
        }

        return true
      })
      ?.filter((g) => !serverId || serverId.length === 0 || g.serverId === serverId)
      ?.map((g) => {
        if (companyId && companyId?.length > 0) {
          return {
            ...g,
            companies: g.companies.filter((c) => c.companyId === companyId)
          }
        }

        return g
      })
      ?.map((g) => {
        if (queueSize && queueSize?.length > 0) {
          return {
            ...g,
            companies: g.companies.filter((c) => c.queueSize > 0)
          }
        }

        return g
      })

    const gatewayFilter = (g: any) => {
      let companyResult = true
      let queueResult = true

      if (companyId) {
        companyResult = g.companyId === companyId
      }

      if (queueSize && queueSize.length > 0) {
        let size = 0

        if (queueSize === RECEIVERS_QUEUE_SIZE_OPTION_5.value) {
          size = 4
        }

        if (queueSize === RECEIVERS_QUEUE_SIZE_OPTION_10.value) {
          size = 9
        }

        queueResult = g.queueSize > size
      }

      return companyResult && queueResult
    }

    return {
      currentAzKey: data?.currentAzKey,
      gateways,
      parsed: [
        {
          key: 'all',
          data: gateways?.reduce((acc: IGatewayCompanyModel[], curr) => [...acc, ...curr.companies], []).filter(gatewayFilter),
          Icon: commonIcons.receiverAll
        },
        {
          key: 'online',
          data: gateways
            ?.reduce((acc: IGatewayCompanyModel[], curr) => {
              const onlineCompanies: IGatewayCompanyModel[] = curr.companies.filter((c) => c.paths.every((p) => p.online))

              return [...acc, ...onlineCompanies]
            }, [])
            .filter(gatewayFilter),
          Icon: commonIcons.receiverOnline
        },
        {
          key: 'buffer',
          data: gateways
            ?.reduce((acc: IGatewayCompanyModel[], curr) => {
              const bufferCompanies: IGatewayCompanyModel[] = curr.companies.filter((c) => c.queueSize > 0)

              return [...acc, ...bufferCompanies]
            }, [])
            .filter(gatewayFilter),
          Icon: commonIcons.stack
        },
        {
          key: 'offline',
          data: gateways
            ?.reduce((acc: IGatewayCompanyModel[], curr) => {
              const offlineCompanies: IGatewayCompanyModel[] = curr.companies.filter((c) => !c.companyOnline)

              return [...acc, ...offlineCompanies]
            }, [])
            .filter(gatewayFilter),
          Icon: commonIcons.receiverOffline
        },

        {
          key: 'degraded',
          data: gateways
            ?.reduce((acc: IGatewayCompanyModel[], curr) => {
              const onlinePartialCompanies: IGatewayCompanyModel[] = curr.companies.filter((c) => {
                const online = c.paths.some((p) => p.online)
                const offline = c.paths.some((p) => !p.online)

                return online && offline
              })

              return [...acc, ...onlinePartialCompanies]
            }, [])
            .filter(gatewayFilter),
          Icon: commonIcons.receiverDegraded
        }
      ]
    }
  }, [data, serverId, companyId, queueSize])

  useEffect(() => {
    setData(cloudReceivers || null)
  }, [cloudReceivers, filterData, setData])

  //   const buildFilterHeader = () => {
  //     let header: any = null

  //     let extras = []

  //     if (tab && tab !== 'gateways') {
  //       extras.push(tab)
  //     }

  //     if (serverId) {
  //       extras.push(serverId)
  //     }

  //     if (companyId) {
  //       const cy = companies.find((c) => c.value === companyId)
  //       if (cy) {
  //         extras.push(cy.label)
  //       }
  //     }

  //     if (queueSize) {
  //       extras.push(`queue ${queueSize}`)
  //     }

  //     if (extras.length > 0) {
  //       header = (
  //         <div className='flex mt-1'>
  //           <div className='px-3 text-text/70 gap-2 bg-surface border border-border rounded-2xl py-1 text-[0.725rem] sm:text-[0.8rem]'>
  //             <span className='jost-medium'>Filtered by: </span>
  //             <span className='capitalize'>{extras.join(' / ')}</span>
  //           </div>
  //         </div>
  //       )
  //     }

  //     return header
  //   }

  //   const filterHeader = buildFilterHeader()

  const loading = isLoading || isError || !data || !cloudReceivers

  return (
    <TimerProvider>
      <Header>
        <div className='max-w-[1330px]'>
          <Navbar transparent startComponents={<NavbarCompanyLogo />} icons={<RootHeaderButtons />} />
          <Breadcrumbs route={ROUTE_RECEIVERS} />
          <div className='flex items-start justify-between'>
            <PageTitle>Receivers</PageTitle>
            <ReceiversFilterButton receivers={data} />
          </div>
          <VertSpacerMd className='hidden md:block' />
          <VertSpacerLg className='block md:hidden' />
          <div className='flex items-center justify-between'>
            <ReceiversRefetch data={cloudReceivers} loading={loading || isRefetching} refetch={refetch} />
            <PageClearFilterButton filter={filter} filterKeyWordsFunc={getFilteredByKeywords} callback={clear} />
          </div>
          {/* {filterHeader} */}
        </div>
      </Header>
      <LayoutContainer bgClasses='max-w-[1400px]'>
        <LayoutPadding topPadding='scrollbar-none pt-[60px] sm:pt-[100px] md:pt-[80px] lg:pt-[70px]'>
          <PullRefresh loading={loading} callback={refetch} />
          <div className='relative flex items-start justify-between gap-[20px]'>
            <div className='max-w-full grow'>
              {tab === 'gateways' && (
                <div className='grid lg:grid-cols-2 gap-[20px]'>
                  <ReceiverGatewayView loading={loading} data={data} />
                </div>
              )}
              {tab !== 'gateways' && (
                <ReceiverCompanyView
                  key={`receiver-gateway-view-${tab}`}
                  loading={loading}
                  data={data}
                  condition={receiverParams.find((p) => p.key === tab)?.condition}
                />
              )}
            </div>
            <ReceiverKpiContainer loading={loading} className='hidden md:grid' />
          </div>
        </LayoutPadding>
      </LayoutContainer>
    </TimerProvider>
  )
}
